<template>
  <div id="service-detail-container">
    <div v-show="loading">
      <LoadingSpinner class="loading-spinner">
      </LoadingSpinner>
    </div>
    <div id="detail-container" class="detail-container">
      <BreadCrumbs v-if="hasProduct || hasBundle" :step=2></BreadCrumbs>
      <div v-if=!ecommEnabled class="alert" :class="brand.name">
        <p>The selected online-purchase special is no longer available.</p>
        <p>Please contact <b>{{ dealer.name }}</b> to learn more about available specials for your vehicle at
        <a v-bind:href="`tel:${bdcPhoneCompact}`" v-on:click="trackBdcPhone" class="alert">{{ dealer.phone.bdc | phoneFormatter }}</a>.</p>
      </div>
      <div class="service-detail-container" v-else>
        <div v-if="hasProduct" class="product-page-container">
          <div class="product-description">
            <div class="sticky-title">{{ product.title }}</div>
            <img class="product-img" id="product-img" alt="product image" :class="brand.name" :src="imageUrl">
            <div class="description-container" :class="brand.name">
              <div v-html="product.description"></div>
              <div class="disclaimer" :class="brand.name" v-html="product.disclaimer"></div>
            </div>
          </div>
          <div class="variant-outer-container" id="variant-outer-container">
            <div class="variant-container" id="variant-container">
              <VariantSelection class="variant" :brand="brand" :dealer="dealer"
              :product="product" :customer="customer" :step="getCurrentStep()"
              productType="product"
              :category="product.category"
              v-on:image_change="refreshImage($event)"
              ></VariantSelection>
            </div>
          </div>
        </div>
        <div v-if="hasBundle" class="product-page-container">
          <div class="product-description">
            <div :class="bundle.title" class="sticky-title">{{ bundle.title }}</div>
            <img class="product-img" id="product-img"  alt="bundle image" :class="brand.name" :src="imageUrl">
            <div class="description-container" :class="brand.name">
              <div :class="bundle.description" v-html="bundle.description"></div>
              <div class="product-title">{{ bundleSubTitle }}</div>
              <div v-for="(product, index) in productArray" :key="index">
                <a v-on:click="showProduct(product.product_id)" :class="brand.name" class="product-title-link" link-classes="text-nowrap cursor:pointer">{{ product.title }}</a>
              </div>
              <div class="item-rectangle"/>
              <div :id="`product-grid-container-${product.product_id}`" class="product-grid-container" v-for="(product) in productArray" :key="product.id">
                <div class="product-title">{{ product.title }}</div>
                <div>
                  <div v-html="product.description"></div>
                  <div class="disclaimer" :class="brand.name" v-html="product.disclaimer"></div>
                </div>
                <div class="item-rectangle"/>
              </div>
            </div>
          </div>
          <div class="variant-outer-container" id="variant-outer-container">
            <div class="variant-container" id="variant-container">
              <VariantSelection class="variant" :brand="brand" :dealer="dealer" :product="bundle" :customer="customer" :step="getCurrentStep()" productType="bundle"></VariantSelection>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import VariantSelection from '@/components/ecomm/product/VariantSelection'
import LoadingSpinner from '@/components/LoadingSpinner'
import BreadCrumbs from '@/components/ecomm/BreadCrumbs'
import { useBreadcrumbService } from '@/services/breadcrumb-service'
import Tracking from '@/mixins/tracking-mixin'
import { ImageModel } from '@/models/image-model'

export default {
  name: 'ServiceDetail',
  mixins: [Tracking],
  data: function () {
    const { resetBreadcrumb, getCurrentStep, setSteps } = useBreadcrumbService()
    return {
      loading: false,
      hasProduct: false,
      hasBundle: false,
      product: null,
      resetBreadcrumb,
      getCurrentStep,
      setSteps,
      imageUrl: ''
    }
  },
  components: {
    VariantSelection,
    LoadingSpinner,
    BreadCrumbs
  },
  created () {
    this.fetchData()
  },
  computed: {
    ...mapState({
      brand: state => state.brand,
      dealer: state => state.dealer,
      customer: state => state.customer
    }),
    ...mapGetters({
      listProducts: ['productVariants/listProducts'],
      listBundles: ['productVariants/listBundles'],
      ecommEnabled: 'ecommEnabled' // this page is visible through all ecoomm paths
    }),
    getItemType () {
      let itemType
      if (this.$route.params && this.$route.params.id) {
        if (this.$route.params.id.toUpperCase().substring(0, 2) === 'BD') {
          itemType = 'bundle'
        } else { // default to product
          itemType = 'product'
        }
      }

      return itemType
    },
    bundleSubTitle () {
      return `Package includes (${this.bundledProductCount}) Bundled Service Items:`
    }
  },
  methods: {
    refreshImage (eventValue) {
      if (eventValue.length > 0) {
        this.imageUrl = ImageModel.makeProductImageUrl({ brandName: this.brand.name, imageName: eventValue })
      } else {
        this.imageUrl = ImageModel.makeProductImageUrl({ brandName: this.brand.name, imageName: this.product.image_name })
      }
    },
    async fetchData () {
      this.loading = true
      if (this.getItemType === 'bundle') {
        await this.fetchBundleData()
        if (this.hasBundle) {
          this.setSteps({ currentStep: 2, category: this.bundle.category })
        }
      }

      if (this.getItemType === 'product' || !this.hasBundle) {
        await this.fetchProductData()
        if (this.hasProduct) {
          this.setSteps({ currentStep: 2, category: this.product.category })
        }
      }

      if (!this.hasBundle && !this.hasProduct) {
        this.$nextTick(() => { this.$router.replace({ name: 'servicelist' }) })
      }

      const serviceDetailContainer = document.getElementById('service-detail-container')
      if (serviceDetailContainer && serviceDetailContainer.offsetTop) {
        document.documentElement.style.setProperty('--offset', `${serviceDetailContainer.offsetTop}px`)
      }

      // Set image only if we have not matched to a variant level option image
      if (!this.imageUrl) {
        const image = this.hasBundle ? this.bundle.image_name : this.hasProduct ? this.product.image_name : ''
        this.imageUrl = ImageModel.makeProductImageUrl({ brandName: this.brand.name, imageName: image })
      }
      this.scrollIntoViewSmooth('detail-container')
      this.loading = false
    },
    async fetchProductData () {
      this.product = await this.$store.dispatch('productVariants/getCurrentProduct', this.$route.params.id)
      if (this.product && Object.keys(this.product).length > 0) {
        this.logEvent(this.product.title)
        this.hasProduct = true
      } else {
        this.hasProduct = false
      }
    },
    async fetchBundleData () {
      this.bundle = await this.$store.dispatch('productVariants/getCurrentBundle', this.$route.params.id)
      if (this.bundle && Object.keys(this.bundle).length > 0) {
        this.productArray = []
        this.bundledProductCount = this.bundle.products ? this.bundle.products.length : 0
        for (let i = 0; i < this.bundle.products.length; i++) {
          const productDetail = await this.$store.dispatch('productVariants/getCurrentProduct', this.bundle.products[i])
          productDetail.imageUrl = ImageModel.makeProductImageUrl({ brandName: this.brand.name, imageName: productDetail.image_name })
          this.productArray.push(productDetail)
        }
        this.logEvent(this.bundle.title)
        this.hasBundle = true
      } else {
        this.hasBundle = false
      }
    },
    logEvent (title) {
      // set the known product information before we log the interaction
      this.$store.commit('product/setProduct', { result: { code: this.$route.params.id, title: title } })
      this.$store.dispatch('session/logInteraction', { interaction_type: 'eshop', action: 'view_item', metadata: { code: this.$route.params.id, title: title } })
      this.$store.dispatch('track/gaEcommViewEvent', { vm: this, event: 'view_item', brand: this.brand.name, items: [{ code: this.$route.params.id, title: title }] })
      this.$store.dispatch('track/facebookEvent', { event: 'View-Item' })
    },
    showProduct (id) {
      this.scrollIntoViewSmooth('product-grid-container-' + id)
    },
    scrollIntoViewSmooth (containerDiv) {
      // scrollIntoView smoothly
      const focusDiv = document.getElementById(containerDiv)
      if (focusDiv && focusDiv.offsetTop) {
        const y = focusDiv.offsetTop - (window.innerHeight / 3)
        window.scrollTo(
          {
            top: y,
            left: 0,
            behavior: 'smooth'
          }
        )
      }
    }
  }
}
</script>

<style scoped>
.product-page-container {
  animation: fadeInLoad 2s;
}
@keyframes fadeInLoad {
  from {
    opacity:0;
  }
  to {
    opacity:1;
  }
}
.service-detail-container {
  display: flex;
  width: 100%;
  justify-items: center;
  align-content: center;
}
.product-page-container {
  width: 100%;
  margin: 0px 20px 20px;
  display: flex;
  flex-direction: column;
  text-align: left;
  column-gap: 20px;
  row-gap: 20px;
  flex-wrap: wrap;
}
.description-container {
  margin-top: 20px;
}
.product-description {
  flex-basis: 0;
  flex-grow: 999;
  min-width: 40%;
}
.sticky-title {
  padding: 25px 0px 20px;
  font-weight: bold;
  font-size: 2em;
  flex-grow: 1;
  flex-basis: 300px;
  width: 100%;
  align-self: start;
  position: sticky; /* overflow has to be visible for all ancestor elements */
  position: -webkit-sticky; /* needed for Safari */
  top: var(--offset);
  background-color: white;
  border: none;
}
.variant-outer-container {
  flex-grow: 1;
  flex-basis: 300px;
  width: 100%;
  align-self: start;
  position: sticky; /* overflow has to be visible for all ancestor elements */
  position: -webkit-sticky; /* needed for Safari */
  top: var(--offset);
  padding-top: 90px;
}
.variant-container {
  flex-grow: 1;
  flex-basis: 300px;
  width: 100%;
  align-self: start;
  position: sticky; /* overflow has to be visible for all ancestor elements */
  position: -webkit-sticky; /* needed for Safari */
  top: var(--offset);
  padding: 20px;
  box-shadow: 0px 0px 5px 1px rgb(33 37 41 / 11%);
  border: 1px solid #E6E6E6;
  border-radius: 3px;
  border-width: thin;
}
.variant {
  display: grid;
  grid-template: auto 1fr auto;
  max-height: 500px;
  overflow-y: auto;
  position: relative;
}
.variant-container .variant {
  max-height: calc(100vh - var(--offset));
}
.product-img {
  width: 100%;
}
.product-title {
  font-weight: bold;
  margin: 20px 0px;
}
.disclaimer {
  margin-top: 20px;
  font-size: 0.5rem;
  text-align: center;
}
.disclaimer.bmw {
  text-align: left;
}
.item-rectangle {
  width: 90%;
  height: 1px;
  background: #000000;
  opacity: 0.08;
  margin: 40px 20px 0px;
  display: flex;
  justify-self: center;
  justify-items: center;
  text-align: center;
  align-items: center;
}
.alert {
  color: red;
}
@media only screen and (min-width: 576px) {
}
@media only screen and (min-width: 768px) {
}
@media only screen and (min-width: 992px) {
  .service-detail-container {
    justify-self: center;
    justify-content: center;
    justify-items: center;
  }
  .product-page-container {
    column-gap: 50px;
    flex-direction: row;
  }
  .product-description {
    max-width: 50%;
  }
}
@media only screen and (min-width: 1200px) {
  .product-page-container {
    max-width: 1200px;
  }
}
</style>
